import { StripeBillingCustomerPortal } from '../Payments/StripeCustomerPortal';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscription } from '../../store/actions/payments';
import { useEffect } from 'react';
import moment from 'moment';
import { CheckCircleFilled } from '@ant-design/icons';
import UpdateSubscription from './UpdateSubscription';
import { plans } from '../Payments/PlanSelection';
import { useAuth } from '@frontegg/react';
import { listPractitioners } from 'src/store/actions/practitioner.action';
import { Tag } from 'antd';
import { analytics } from 'src/services';

const PlanFeaturesList = ({ features }) => (
  <ul className="space-y-2 mt-3 text-sm">
    {features.map((item, index) => (
      <li key={index} className="flex items-start">
        <CheckCircleFilled className="mr-2 text-green-500 mt-1 flex-shrink-0 text-xs" />
        <span>{item.feature}</span>
      </li>
    ))}
  </ul>
);

const _PlanFeatures = ({ subscription }) => {
  if (!subscription?.plan || !plans[subscription.plan]) {
    return null;
  }

  const plansToShow = [];
  if (subscription.plan === 'teams') {
    plansToShow.push('teams', 'pro', 'free');
  } else if (subscription.plan === 'pro') {
    plansToShow.push('pro', 'free');
  } else {
    plansToShow.push('free');
  }

  const allFeaturesToShow = plansToShow.flatMap((planKey) => {
    if (!plans[planKey]) return [];
    const features =
      planKey === 'pro' || planKey === 'teams'
        ? plans[planKey].features.slice(1)
        : plans[planKey].features;
    return features.map((feature) => ({ ...feature, planKey }));
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {allFeaturesToShow.map((item, index) => (
        <div
          key={`${item.planKey}-${item.feature}-${index}`}
          className="flex items-start"
        >
          <CheckCircleFilled className="mr-2 text-green-500 mt-1 flex-shrink-0" />
          <div>
            <div className="font-medium">{item.feature}</div>
            {item.description && (
              <div className="text-sm text-gray-600">{item.description}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Plan Summary Component
const PlanSummary = ({ subscription, formatNextBillingDate, dispatch }) => {
  return (
    <>
      <h3 className="text-black font-bold text-[18px]">Current Plan</h3>
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-medium text-gray-800">
              {plans[subscription?.plan]?.name || 'Free'} Plan
            </h2>
            {subscription?.plan === 'teams' && (
              <div className="flex items-center mt-2">
                <div className="text-sm text-gray-600">
                  Number of paid seats:
                  <div className="flex items-center mt-1">
                    <div className="text-xl font-bold">
                      {subscription?.quantity}
                    </div>
                    <div className="ml-2">
                      <UpdateSubscription
                        subscription={subscription}
                        onSuccess={() => dispatch(getSubscription())}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {subscription?.plan === 'free' && (
              <div className="mt-1 flex items-baseline">
                <span className="text-3xl font-bold">$0</span>
                <span className="text-gray-500 ml-2">Free Forever</span>
              </div>
            )}
          </div>

          <div className="text-right">
            {subscription?.plan !== 'free' && (
              <>
                <div className="text-sm text-gray-500">Next invoice on</div>
                <div className="font-medium">{formatNextBillingDate()}</div>
                {subscription?.upcoming_invoice?.amount_due && (
                  <>
                    <div className="text-sm text-gray-500 mt-2">Amount</div>
                    <div className="font-medium">
                      ${subscription.upcoming_invoice.amount_due.toFixed(2)}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Seat Allocation Component
const SeatAllocation = ({ getUsersByRole }) => {
  return (
    <div className="mb-6">
      <h3 className="text-black font-bold text-[18px]">Seat Allocation</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(getUsersByRole()).map(([roleName, users]) => {
          // Skip roles with no users
          if ((users.paid?.length || 0) + (users.free?.length || 0) === 0)
            return null;

          return (
            <div
              key={roleName}
              className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-md font-medium">{roleName}</div>
                <div>
                  {users.paid?.length > 0 && (
                    <Tag color="#00d090" className="mr-1">
                      Paid
                    </Tag>
                  )}
                  {users.free?.length > 0 && <Tag color="default">Free</Tag>}
                </div>
              </div>

              {users.paid?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xl font-bold mb-2">
                    {users.paid.length}
                  </div>
                </div>
              )}

              {users.free?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xl font-bold mb-2">
                    {users.free.length}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Available Plans Component
const AvailablePlans = ({ subscription }) => {
  return (
    <>
      <h3 className="text-black font-bold text-[18px]">Available Plans</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(plans).map((planKey) => (
          <div
            key={planKey}
            className={`p-4 rounded-lg border flex flex-col ${
              subscription?.plan === planKey
                ? 'border-[#00d090] bg-[#00d090]/10'
                : 'border-gray-200'
            }`}
          >
            <div>
              <h4 className="text-xl font-medium">{plans[planKey].name}</h4>
              <div className="text-2xl font-bold my-2">
                {plans[planKey].price > 0 ? `$${plans[planKey].price}` : 'Free'}
              </div>
              <div className="text-sm text-gray-500 mb-3">
                {plans[planKey].price > 0
                  ? `per user / ${plans[planKey].interval || 'month'}`
                  : 'Free Forever'}
              </div>
            </div>

            <div className="flex-grow">
              <PlanFeaturesList features={plans[planKey].features} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// Payment Information Component
const PaymentInformation = ({ subscription }) => {
  return (
    subscription?.plan !== 'free' && (
      <div className="mt-6">
        <h3 className="text-black font-bold text-[18px]">
          Payment Information
        </h3>

        <div className="flex justify-between items-center py-3">
          <div className="flex items-center">
            <span>Payment method</span>
          </div>
          <div className="flex items-center">
            <StripeBillingCustomerPortal
              name="Change payment method"
              type="button"
            />
          </div>
        </div>
      </div>
    )
  );
};

export const Billing = () => {
  const { subscription, subscriptionLoading } = useSelector(
    (state) => state.paymentsState
  );
  const { practitionerList } = useSelector((state) => state?.practitionerState);
  const { user } = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    analytics.track('Viewed Billing Tab In Settings', { email: user?.email });
    dispatch(getSubscription());
    dispatch(listPractitioners(true));
  }, [dispatch, user?.email]);

  const getUsersByRole = () => {
    if (!practitionerList?.length) return {};

    // Get all unique roles
    const allRoles = new Set();
    practitionerList.forEach((practitioner) => {
      practitioner.frontegg_roles?.forEach((role) => {
        allRoles.add(role.name);
      });
    });

    // Create a map of users by role
    const usersByRole = {};
    Array.from(allRoles).forEach((roleName) => {
      usersByRole[roleName] = {
        paid: practitionerList.filter((user) =>
          user.frontegg_roles?.some(
            (role) => role.is_paid && role.name === roleName
          )
        ),
        free: practitionerList.filter((user) =>
          user.frontegg_roles?.some(
            (role) => !role.is_paid && role.name === roleName
          )
        ),
      };
    });

    return usersByRole;
  };

  // Format date for next billing period
  const formatNextBillingDate = () => {
    if (!subscription?.current_period_end) return 'N/A';
    return moment.unix(subscription?.current_period_end).format('MMM DD, YYYY');
  };

  if (subscriptionLoading) {
    return (
      <div className="mt-5 lg:w-2/3">
        <div className="settings-component flex flex-col p-5 shadow-xl bg-white rounded-xl">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-4">
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 lg:w-2/3">
      <div className="bg-white rounded-xl shadow-sm p-5">
        {/* Plan Summary */}
        <PlanSummary
          subscription={subscription}
          formatNextBillingDate={formatNextBillingDate}
          dispatch={dispatch}
        />

        {/* Seat Allocation */}
        <SeatAllocation getUsersByRole={getUsersByRole} />

        {/* Available Plans */}
        <AvailablePlans subscription={subscription} />

        {/* Payment Information */}
        <PaymentInformation subscription={subscription} />
      </div>
    </div>
  );
};

export default Billing;
